import { Spacer } from "@hackthenorth/north";
import {
  ArrowLeftIcon,
  MinusSmallIcon,
  PlusSmallIcon,
} from "@heroicons/react/24/outline";
import debounce from "lodash.debounce";
import moment from "moment";
import { Button, Select, Text, TextArea, TextInput } from "north.js";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import styled from "styled-components";

import { QueryMode } from "src/api/types.generated";
import { PageWrapper } from "src/shared/components";
import Autocomplete from "src/shared/components/Autocomplete";
import { EVENT_ID } from "src/shared/constants/event";

import "twin.macro";

import {
  TPrimaryRoles,
  TSupplementaryRoles,
} from "../../shared/constants/roles";
import { useAddShiftContext } from "../../shared/contexts/AddShiftContext";
import { convertDayToDate } from "../../shared/contexts/constants";
import { useGetShiftLeadsLazyQuery } from "../graphql/getShiftLeads.generated";

const AddVolunteerShiftPage = () => {
  const navigate = useNavigate();

  const [shiftNameTouched, setShiftNameTouched] = useState(false);
  const [shiftLeadTouched, setShiftLeadTouched] = useState(false);
  const [locationTouched, setLocationTouched] = useState(false);
  const [timeFrameTouched, setTimeFrameTouched] = useState(false);
  const [volunteersTouched, setVolunteersTouched] = useState(false);

  const {
    shiftName,
    shiftDateStart,
    shiftDateEnd,
    timeStart,
    timeEnd,
    location,
    shiftLead,
    shiftLeadId,
    roles,
    supplementaryType,
    requiredVolunteers,
    minVolunteers,
    description,

    updateShiftName,
    updateShiftDateStart,
    updateShiftDateEnd,
    updateTimeStart,
    updateTimeEnd,
    updateLocation,
    updateShiftLead,
    updateRoles,
    updateSupplementaryType,
    updateRequiredVolunteers,
    updateMinVolunteers,
    updateDescription,

    createShift,
  } = useAddShiftContext();

  const [getShiftLead, shiftLeadResult] = useGetShiftLeadsLazyQuery();

  // The unfiltered list shown before typing, and again whenever the box is
  // cleared — otherwise the dropdown keeps showing the last search's results.
  const fetchDefaultShiftLeads = useCallback(() => {
    getShiftLead({
      variables: {
        where: {
          OR: [
            {
              event_attendees: {
                some: {
                  event_id: { equals: EVENT_ID },
                  volunteer: {
                    is: {
                      is_lead: {
                        equals: true,
                      },
                    },
                  },
                },
              },
            },
            {
              email: {
                endsWith: "hackthenorth.com",
                mode: QueryMode.Insensitive,
              },
            },
          ],
        },
        take: 20,
      },
    });
  }, [getShiftLead]);

  useEffect(() => {
    fetchDefaultShiftLeads();
  }, [fetchDefaultShiftLeads]);

  useEffect(() => {
    if (!roles) {
      updateRoles(Object.values(TPrimaryRoles)[0]);
    }

    if (!timeStart) {
      updateTimeStart("00:00");
    }

    if (!timeEnd) {
      updateTimeEnd("00:00");
    }

    if (!shiftDateStart) {
      updateShiftDateStart("Friday");
    }

    if (!shiftDateEnd) {
      updateShiftDateEnd("Friday");
    }
  }, [
    roles,
    timeStart,
    timeEnd,
    shiftDateStart,
    shiftDateEnd,
    updateRoles,
    updateTimeStart,
    updateTimeEnd,
    updateShiftDateStart,
    updateShiftDateEnd,
  ]);

  const validTimeFrame = (
    startDate: string,
    endDate: string,
    startTime: string,
    endTime: string
  ) => {
    const startDateTime = moment(
      `${convertDayToDate(startDate)} ${startTime}`,
      "YYYY-MM-DD HH:mm"
    );
    const endDateTime = moment(
      `${convertDayToDate(endDate)} ${endTime}`,
      "YYYY-MM-DD HH:mm"
    );
    return startDateTime.isBefore(endDateTime);
  };

  // Time Start and Time End selection choices
  const timeChoices = useMemo(() => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const time = `${hour.toString().padStart(2, "0")}:${minute
          .toString()
          .padStart(2, "0")}`;
        times.push(time);
      }
    }
    return times;
  }, []);

  const handleAddItem = useCallback(
    (e) => {
      e.preventDefault();

      setShiftNameTouched(true);
      setShiftLeadTouched(true);
      setLocationTouched(true);
      setTimeFrameTouched(true);
      setVolunteersTouched(true);

      if (
        shiftName.length < 2 ||
        !shiftDateStart ||
        !shiftDateEnd ||
        !timeStart ||
        !timeEnd ||
        !location ||
        shiftLeadId === null ||
        minVolunteers > requiredVolunteers ||
        !validTimeFrame(shiftDateStart, shiftDateEnd, timeStart, timeEnd)
      ) {
        return;
      }

      createShift()
        .then(() => {
          navigate("/volunteer/shifts");
        })
        // Stay on the form so the shift can be retried; the failure is toasted.
        .catch(() => undefined);
    },
    [
      createShift,
      navigate,
      shiftName,
      shiftDateStart,
      shiftDateEnd,
      timeStart,
      timeEnd,
      location,
      shiftLeadId,
      minVolunteers,
      requiredVolunteers,
    ]
  );

  // Memoised so the debounce timer survives re-renders and can be cancelled.
  const debouncedGetShiftLead = useMemo(
    () =>
      debounce((searchValue: string) => {
        if (searchValue === "") return;
        getShiftLead({
          variables: {
            where: {
              OR: [
                {
                  AND: [
                    {
                      event_attendees: {
                        some: {
                          event_id: { equals: EVENT_ID },
                          volunteer: {
                            is: {
                              is_lead: {
                                equals: true,
                              },
                            },
                          },
                        },
                      },
                    },
                    {
                      name: {
                        contains: searchValue,
                        mode: QueryMode.Insensitive,
                      },
                    },
                  ],
                },
                {
                  AND: [
                    {
                      name: {
                        contains: searchValue,
                        mode: QueryMode.Insensitive,
                      },
                    },
                    {
                      // Match by name; only require an @hackthenorth.com email
                      // (do not also require the email to contain the search text).
                      email: {
                        endsWith: "hackthenorth.com",
                        mode: QueryMode.Insensitive,
                      },
                    },
                  ],
                },
              ],
            },
            take: 20,
          },
        });
      }, 500),
    [getShiftLead]
  );

  return (
    <PageWrapper>
      <AddVolunteerShiftContainer>
        <div tw="flex flex-row gap-4 items-center">
          <ArrowLeftIcon tw="h-16 font-bold text-text-secondary cursor-pointer" />
          <Text
            type="button"
            tw="font-bold text-text-secondary cursor-pointer"
            color="secondary"
            onClick={() => navigate("/volunteer/shifts")} // TODO: FIX
          >
            Back to Shifts
          </Text>
        </div>

        {/* </Button> */}
        <Spacer height={24} />

        <Text type="h2">Add a shift</Text>
        <Spacer height={8} />

        <Text type="body1">
          Add, edit, or delete shifts by filling the form below.
        </Text>
        <Spacer height={24} />

        {/* FORM */}
        <StyledForm onSubmit={handleAddItem}>
          <InputContainer>
            <Text type="body1" tw="font-bold">
              Shift name
            </Text>

            <TextInput
              type="text"
              placeholder="Enter a name..."
              tw="text-text-secondary font-medium h-48 w-full!"
              onChange={(e) => updateShiftName(e.target.value)}
              value={shiftName}
              error={
                shiftNameTouched && shiftName.length <= 2
                  ? "Shift name is too short"
                  : ""
              }
              onBlur={() => setShiftNameTouched(true)}
            />
          </InputContainer>

          <div tw="flex flex-row w-full gap-48">
            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Start Date
              </Text>

              <Select
                tw="h-48 text-text-secondary font-medium"
                fullWidth
                onChange={(e: any) => updateShiftDateStart(e.target.value)}
                value={shiftDateStart}
                placeholder="Enter a start date"
                error={
                  !validTimeFrame(
                    shiftDateStart,
                    shiftDateEnd,
                    timeStart,
                    timeEnd
                  ) && timeFrameTouched
                    ? " "
                    : ""
                }
              >
                <Select.Option value={"Friday"}>Friday</Select.Option>
                <Select.Option value={"Saturday"}>Saturday</Select.Option>
                <Select.Option value={"Sunday"}>Sunday</Select.Option>
              </Select>
            </InputContainer>

            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                End Date
              </Text>

              <Select
                tw="h-48 text-text-secondary font-medium"
                fullWidth
                onChange={(e: any) => updateShiftDateEnd(e.target.value)}
                value={shiftDateEnd}
                placeholder="Enter an end date"
                error={
                  !validTimeFrame(
                    shiftDateStart,
                    shiftDateEnd,
                    timeStart,
                    timeEnd
                  ) && timeFrameTouched
                    ? " "
                    : ""
                }
              >
                <Select.Option value={"Friday"}>Friday</Select.Option>
                <Select.Option value={"Saturday"}>Saturday</Select.Option>
                <Select.Option value={"Sunday"}>Sunday</Select.Option>
              </Select>
            </InputContainer>
          </div>

          <div tw="flex flex-row w-full gap-48">
            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Time Start
              </Text>

              <Select
                fullWidth
                tw="h-48 text-text-secondary font-medium"
                value={timeStart}
                onChange={(e) => {
                  updateTimeStart(e.target.value);
                }}
                error={
                  !validTimeFrame(
                    shiftDateStart,
                    shiftDateEnd,
                    timeStart,
                    timeEnd
                  ) && timeFrameTouched
                    ? "Shift start must be fore shift end"
                    : ""
                }
              >
                {timeChoices.map((time) => (
                  <Select.Option key={time} value={time}>
                    {time}
                  </Select.Option>
                ))}
              </Select>
            </InputContainer>

            <InputContainer tw="flex-1">
              <Text type="body1" tw="font-bold">
                Time End
              </Text>

              <Select
                fullWidth
                tw="h-48 text-text-secondary font-medium"
                value={timeEnd}
                onChange={(e) => {
                  updateTimeEnd(e.target.value);
                }}
                error={
                  !validTimeFrame(
                    shiftDateStart,
                    shiftDateEnd,
                    timeStart,
                    timeEnd
                  ) && timeFrameTouched
                    ? " "
                    : ""
                }
              >
                {timeChoices.map((time) => (
                  <Select.Option key={time} value={time}>
                    {time}
                  </Select.Option>
                ))}
              </Select>
            </InputContainer>
          </div>

          <InputContainer>
            <Text type="body1" tw="font-bold">
              Location
            </Text>

            <TextInput
              type="text"
              placeholder="Enter location"
              tw="text-text-secondary font-medium h-48 w-full!"
              width={"700px"}
              onChange={(e) => updateLocation(e.target.value)}
              error={
                locationTouched && location === "" ? "Location is required" : ""
              }
              onBlur={() => setLocationTouched(true)}
            />
          </InputContainer>

          <InputContainer>
            <Text type="body1" tw="font-bold">
              Shift Lead
            </Text>

            <Autocomplete
              tw="text-text-secondary font-medium h-48 w-full!"
              options={
                shiftLeadResult.data?.users.map((user) => ({
                  label: `${user.name} (${user.email})`,
                  // Identify the lead by id as names are not unique.
                  value: user.id,
                })) || []
              }
              loading={shiftLeadResult.loading}
              handleSelect={(value) => {
                const selected = shiftLeadResult.data?.users.find(
                  (user) => user.id === Number(value)
                );
                updateShiftLead(selected?.name ?? "", selected?.id ?? null);
              }}
              handleTextInputChange={(value) => {
                if (value === "") {
                  // Drop any in-flight search so it can't land after the reset.
                  debouncedGetShiftLead.cancel();
                  fetchDefaultShiftLeads();
                } else {
                  debouncedGetShiftLead(value);
                }
                // Typing invalidates the previous pick until a new one is made.
                if (shiftLead) {
                  updateShiftLead("", null);
                }
              }}
              renderInput={({ value, onChange }) => (
                <TextInput
                  type="text"
                  placeholder="Enter name"
                  tw="text-text-secondary font-medium h-48 w-full!"
                  value={value}
                  onChange={onChange}
                  onBlur={() => setShiftLeadTouched(true)}
                  error={
                    shiftLeadTouched && shiftLead === ""
                      ? "Shift lead is required"
                      : !shiftLeadResult.loading &&
                        shiftLeadResult.data?.users.length === 0
                      ? `Shift lead with name ${shiftLead} not found`
                      : ""
                  }
                />
              )}
            />
          </InputContainer>

          <InputContainer>
            <Text type="body1" tw="font-bold">
              Roles
            </Text>

            <Select
              fullWidth
              tw="h-48 text-text-secondary font-medium"
              value={roles}
              onChange={(e) => updateRoles(e.target.value)}
            >
              {Object.values(TPrimaryRoles).map((role) => (
                <Select.Option key={role} value={role}>
                  {role}
                </Select.Option>
              ))}
            </Select>
          </InputContainer>

          {roles === TPrimaryRoles.SUPPLEMENTARY && (
            <InputContainer>
              <Text type="body1" tw="font-bold">
                Supplementary Type
              </Text>

              <Select
                fullWidth
                tw="h-48 text-text-secondary font-medium"
                value={supplementaryType}
                onChange={(e) => updateSupplementaryType(e.target.value)}
              >
                {Object.values(TSupplementaryRoles).map((role) => (
                  <Select.Option key={role} value={role}>
                    {role}
                  </Select.Option>
                ))}
              </Select>
            </InputContainer>
          )}

          <div tw="flex flex-row w-full gap-48">
            <InputContainer>
              <Text type="body1" tw="font-bold">
                Preferred Min Volunteers
              </Text>

              <div tw="flex flex-row gap-12 items-center">
                <MinusSmallIcon
                  tw="cursor-pointer"
                  onClick={() => {
                    if (minVolunteers > 0)
                      updateMinVolunteers(minVolunteers - 1);
                  }}
                  height={24}
                />
                <div style={{ width: "48px" }}>
                  <TextInput
                    tw="text-text-secondary font-medium text-center h-48 w-48"
                    value={minVolunteers}
                    error={
                      minVolunteers > requiredVolunteers && volunteersTouched
                        ? " "
                        : ""
                    }
                  />
                </div>

                <PlusSmallIcon
                  tw="cursor-pointer"
                  onClick={() => {
                    updateMinVolunteers(minVolunteers + 1);
                  }}
                  height={24}
                />
              </div>
            </InputContainer>

            <InputContainer>
              <Text type="body1" tw="font-bold">
                Preferred Max Volunteers
              </Text>

              <div tw="flex flex-row gap-12 items-center">
                <MinusSmallIcon
                  tw="cursor-pointer"
                  onClick={() => {
                    if (requiredVolunteers > 0)
                      updateRequiredVolunteers(requiredVolunteers - 1);
                  }}
                  height={24}
                />
                <div>
                  <TextInput
                    tw="text-text-secondary font-medium text-center h-48 w-48"
                    value={requiredVolunteers}
                    error={
                      minVolunteers > requiredVolunteers && volunteersTouched
                        ? " "
                        : ""
                    }
                  />
                </div>

                <PlusSmallIcon
                  tw="cursor-pointer"
                  onClick={() =>
                    updateRequiredVolunteers(requiredVolunteers + 1)
                  }
                  height={24}
                />
              </div>
            </InputContainer>
          </div>
          {minVolunteers > requiredVolunteers && volunteersTouched && (
            <ErrorText>
              Min Volunteers must be less than Max Volunteers
            </ErrorText>
          )}
          <InputContainer>
            <Text type="body1" tw="font-bold">
              Shift Description
            </Text>

            <StyledTextAreaInput
              placeholder="Enter description..."
              tw="text-text-secondary font-medium w-full!"
              value={description}
              fullWidth
              maxChars={500}
              onChange={(e: any) => updateDescription(e.target.value)}
              hint="Add additional details about the shift!"
            />
          </InputContainer>

          <Button color="primary" type="submit">
            <Text>Add Item</Text>
          </Button>
        </StyledForm>

        <Spacer height={64} />
      </AddVolunteerShiftContainer>
    </PageWrapper>
  );
};

const AddVolunteerShiftContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 36px;
  width: 100%;
  max-width: 700px;
`;

const StyledTextAreaInput = styled(TextArea)`
  height: 165px;
  padding: 10px 16px;
`;

const ErrorText = styled(Text).attrs({ mods: "error" })`
  color: ${({ theme }) => theme.globalConstants.color.textDanger};
  font-size: 12px !important;
  margin-top: -36px;
`;

export default AddVolunteerShiftPage;
